import { useEffect, useState } from "react";
import {
    ListContainer,
    ListContext,
    PAGE_DEFAULT,
    PAGE_SIZE_UNLIMITED,
    Page,
    PageRoute,
    SearchBar,
    Stack,
    buildTemplatePath,
} from "@compass/core";
import { ListFooter } from "components";
import { ListFilterBase, Permission, ProviderPositionListDto, ProviderPositionListResource } from "generated/openApi";
import { useInitListFilter, useList } from "hooks";
import { Outlet } from "react-router-dom";
import ApiService from "services/apiService";
import { ProviderPositionListPageHeader } from "./ProviderPositionListPageHeader";
import { ProviderPositionTable } from "./ProviderPositionTable";

const routeName = "providerPosition";

export const providerPositionListPageRoute: PageRoute = {
    handle: "Provider Positions",
    templatePath: buildTemplatePath(routeName),
    getPath: () => buildTemplatePath(routeName),
};

export const ProviderPositionListPage = () => {
    const ROOT_PERMISSION = Permission.MatchUtility;
    const [listItems, setListItems] = useState<ProviderPositionListDto[]>([]);
    const { listFilter, setListFilter } = useInitListFilter<ListFilterBase>({
        sortObjects: [{ property: "name", isDesc: false }],
    });
    const [total, setTotal] = useState<number>();
    const [resource, setResource] = useState<ProviderPositionListResource | null>(null);
    const [searchFocused, setSearchFocused] = useState<boolean>(false);

    const { tableContainerRef, applyPage, applyPageSize, loadList, applySort, applySearch, applySortMethod } = useList({
        listFilter,
        setListFilter,
        fetchList: async (listFilter) => {
            const data = await ApiService.providers.getProviderPositions();
            setListItems(data || []);
        },
        fetchTotal: async (listFilter) => {
            const data = await ApiService.providers.getProviderPositions();
            setTotal(data?.length || 0);
        },
    });

    useEffect(() => {
        loadList(listFilter);
        ApiService.providers.getProviderPositionListResource()
            .then(setResource)
            .catch((err) => {
                console.error("Failed to fetch list resource", err);
            });
    }, []);

    return (
        <Page header={<ProviderPositionListPageHeader rootPermission={ROOT_PERMISSION} handle={providerPositionListPageRoute.handle} />}>
            <ListContainer
                fullHeight
                header={
                    <Stack direction="row" justifyContent="space-between">
                        <SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />
                    </Stack>
                }
                tableContainer={
                    resource && (
                        <ProviderPositionTable
                            rootPermission={ROOT_PERMISSION}
                            tableContainerRef={tableContainerRef}
                            providerPositions={listItems}
                            setProviderPositions={setListItems}
                            resource={resource}
                            searchFocused={searchFocused}
                            sortObjects={listFilter.sortObjects}
                            sort={applySort}
                            loadList={() => loadList(listFilter)}
                        />
                    )
                }
                footer={
                    <ListFooter
                        total={total}
                        size={listFilter.pageSize}
                        page={listFilter.page}
                        onPageChange={applyPage}
                        onSizeChange={applyPageSize}
                        onSortMethodChange={applySortMethod}
                    />
                }
            />
            <ListContext.Provider
                value={{
                    loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }),
                }}
            >
                <Outlet />
            </ListContext.Provider>
        </Page>
    );
};